<template>
  <form class="auth-register-form" @submit.prevent="handleSubmit">
    <BaseInput
      class="mb-3"
      type="text"
      size="xl"
      id="registerName"
      placeholder="Nome"
      v-model="$v.user.name.$model"
      :error="$v.user.name.$error"
      :message="nameErrorMessages"
    />

    <BaseInput
      class="mb-3"
      type="text"
      size="xl"
      id="registerSurname"
      placeholder="Sobrenome"
      v-model="$v.user.surname.$model"
      :error="$v.user.surname.$error"
      :message="surnameErrorMessages"
    />

    <div class="mb-3">
      <BaseInput
        class="mb-0"
        type="text"
        size="xl"
        id="registerNickname"
        placeholder="Nome social"
        v-model="user.nickname"
      />
      <p class="field-help-text">
        Como você prefere ser chamado(a). Não substitui seu nome legal.
      </p>
    </div>

    <!-- Email input with debounce delay -->
    <div
      class="v-input v-input-xl mb-3"
      :class="{ 'v-input-invalid': $v.user.email.$error }"
    >
      <input
        class="v-input-control"
        type="email"
        id="registerEmail"
        placeholder="E-mail"
        v-model="$v.user.email.$model"
        @input="debounce($v.user.email)"
      />
      <div v-if="$v.user.email.$error" class="v-input-invalid-feedback">
        <span v-for="message in emailErrorMessages" :key="message">
          {{ message }}
        </span>
      </div>
    </div>

    <!-- CPF input with debounce delay -->
    <div class="v-input v-input-xl mb-3">
      <input
        class="v-input-control"
        type="tel"
        id="registerCpf"
        placeholder="CPF"
        v-model="$v.user.cpf.$model"
        @input="debounce($v.user.cpf)"
        v-mask="'###.###.###-##'"
      />
      <div v-if="$v.user.cpf.$error" class="v-input-invalid-feedback">
        <span v-for="message in cpfErrorMessages" :key="message">
          {{ message }}
        </span>
      </div>
    </div>

    <BaseInput
      class="mb-3"
      type="tel"
      size="xl"
      id="registerBirthday"
      placeholder="Data de nascimento"
      v-model="$v.user.birthday.$model"
      :error="$v.user.birthday.$error"
      :message="birthdayErrorMessages"
      mask="##/##/####"
    />

    <!-- CPF input with debounce delay -->
    <div class="v-input v-input-xl mb-3">
      <input
        class="v-input-control"
        type="tel"
        id="registerPhoneNumber"
        placeholder="Telefone"
        v-model="$v.user.phone_number.$model"
        @input="debounce($v.user.phone_number)"
        v-mask="['(##) ####-####', '(##) #####-####']"
      />
      <div v-if="$v.user.phone_number.$error" class="v-input-invalid-feedback">
        <span v-for="message in PhoneNumberErrorMessages" :key="message">
          {{ message }}
        </span>
      </div>
    </div>

    <BaseSelect
      class="mb-3"
      size="xl"
      id="registerGender"
      :options="genderOptions"
      v-model="user.gender"
      placeholder="Gênero"
    />

    <BaseInput
      class="mb-3"
      type="password"
      size="xl"
      id="registerPassword"
      placeholder="Nova senha"
      v-model="$v.user.password.$model"
      :error="$v.user.password.$error"
      :message="passwordErrorMessages"
      show-password-toggle
    />

    <BaseInput
      class="mb-4"
      type="password"
      size="xl"
      id="registerPasswordComfirmation"
      placeholder="Repita a senha"
      v-model="$v.user.password_confirmation.$model"
      :error="$v.user.password_confirmation.$error"
      :message="passwordComfirmationErrorMessages"
      show-password-toggle
    />

    <div class="auth-form-user-type">
      <BaseRadio
        class="mb-3 mr-3"
        id="pacientRadio"
        label="Paciente"
        value="patient"
        v-model="userType"
      />
      <BaseRadio
        class="mb-3"
        id="doctorRadio"
        label="Profissional de saúde"
        value="doctor"
        v-model="userType"
      />
    </div>

    <div v-if="userType == 'doctor'">
      <BaseSelect
        class="mb-3"
        size="xl"
        id="registerDoctorCouncil"
        :options="councilOptions"
        v-model="$v.doctor_property_attributes.council_id.$model"
        :error="$v.doctor_property_attributes.council_id.$error"
        :message="doctorCouncilErrorMessages"
        placeholder="Selecione o Conselho"
      />

      <BaseSelect
        class="mb-3"
        size="xl"
        id="registerUf"
        :options="ufsOptions"
        v-model="$v.doctor_property_attributes.council_uf.$model"
        :error="$v.doctor_property_attributes.council_uf.$error"
        :message="doctorCouncilUfErrorMessages"
        placeholder="Selecione o Estado"
      />

      <BaseSelect
        class="mb-3"
        size="xl"
        id="registerDoctorSpeciality"
        :options="specialitiesOptions"
        v-model="$v.doctor_property_attributes.medical_specialty_id.$model"
        :error="$v.doctor_property_attributes.medical_specialty_id.$error"
        :message="doctorSpecialtyErrorMessages"
        placeholder="Selecione a Especialidade"
      />

      <BaseInput
        class="mb-3"
        type="text"
        size="xl"
        id="registerDoctorCouncilNumber"
        placeholder="Número do registro"
        v-model="$v.doctor_property_attributes.council_number.$model"
        :error="$v.doctor_property_attributes.council_number.$error"
        :message="doctorCouncilNumberErrorMessages"
      />

      <BaseInput
        v-if="isPhysicianCouncil"
        class="mb-3"
        type="text"
        size="xl"
        id="registerDoctorRqe"
        placeholder="RQE (registro de especialista)"
        v-model="doctor_property_attributes.rqe"
      />

      <BaseTextarea
        class="mb-4"
        type="text"
        size="xl"
        id="registerDoctorCouncilNumber"
        placeholder="Breve descrição"
        v-model="$v.doctor_property_attributes.short_bio.$model"
        :error="$v.doctor_property_attributes.short_bio.$error"
        :message="doctorShortBioErrorMessages"
      />
    </div>

    <BaseCheckbox
      class="mb-3"
      :value="true"
      :unchecked-value="false"
      v-model="terms"
    >
      <template #title>
        <small class="auth-text auth-form-terms">
          Ao criar uma conta, você está concordando com os
          <a
            href="https://videomedi.com.br/politica-de-privacidade/"
            target="_blank"
          >
            Termos de Uso e a Política de Privacidade.
          </a>
        </small>
      </template>
    </BaseCheckbox>

    <BaseButton
      class="mt-3"
      type="submit"
      size="xl"
      block
      :disabled="!canSubmit || isSubmitting"
    >
      <span class="mr-2">Criar minha conta</span>
      <BaseSpinner v-if="isSubmitting" variant="light" size="sm" />
    </BaseButton>

    <p class="auth-text auth-form-forgot-password">
      Esqueceu a senha?
      <a href="/recuperarSenha">Recuperar</a>
    </p>
  </form>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { required, minLength, email, sameAs } from "vuelidate/lib/validators";

import { cpf } from "../utils/validations/cpf";
import { hasValidChars } from "../utils/validations/email";
import {
  BIRTHDAY_MESSAGES,
  validCalendarDate,
  notFutureDate,
  doctorMinimumAge,
  maxAge
} from "../utils/validations/birthday";
import {
  collectPasswordErrors,
  hasUppercaseLetter,
  hasLowercaseLetter,
  hasNumber,
  hasSpecialCharacter
} from "../utils/validations/password";
import { UFS } from "../utils/constants";
import { isDoctor } from "../utils/userRole";

const touchMap = new WeakMap();

export default {
  name: "LoginForm",
  data() {
    return {
      terms: false,
      userType: "patient",
      ufs: UFS,
      user: {
        name: "",
        surname: "",
        nickname: "",
        email: "",
        cpf: "",
        birthday: "",
        phone_number: "",
        gender: "",
        password: "",
        password_confirmation: ""
      },
      doctor_property_attributes: {
        council_id: "",
        medical_specialty_id: "",
        short_bio: "",
        council_number: "",
        council_uf: "",
        rqe: ""
      },
      redirectFrom: ""
    };
  },
  validations() {
    return {
      user: {
        name: { required },
        surname: { required },
        email: {
          required,
          email,
          hasValidChars,
          async isUnique(value) {
            if (value.includes("@")) {
              await this.emailVerification({ email: value }).then(response => {
                return response.data.status;
              });
            }
            return true;
          }
        },
        cpf: {
          required,
          cpf,
          async isUnique(value) {
            if (value.length == 14) {
              await this.cpfVerification({
                cpf: value
                  .replace("-", "")
                  .replace(".", "")
                  .replace(".", "")
              }).then(response => {
                return response.data.status;
              });
            }
            return true;
          }
        },
        birthday: {
          required,
          validCalendarDate,
          notFutureDate,
          doctorMinimumAge(value) {
            if (this.userType !== "doctor") return true;

            return doctorMinimumAge(value);
          },
          maxAge
        },
        phone_number: {
          required,
          async isUnique(value) {
            if (value.length >= 14) {
              await this.phoneNumberVerification({
                phone_number: value.replace(/[^0-9\.]+/g, "")
              }).then(response => {
                return response.data.status;
              });
            }
            return true;
          }
        },
        password: {
          required,
          minLength: minLength(8),
          hasUppercaseLetter,
          hasLowercaseLetter,
          hasNumber,
          hasSpecialCharacter
        },
        password_confirmation: {
          required,
          minLength: minLength(8),
          confirmPassword: sameAs("password")
        }
      },
      doctor_property_attributes: {
        council_id: { required },
        medical_specialty_id: { required },
        short_bio: { required },
        council_number: { required },
        council_uf: { required }
      }
    };
  },
  mounted() {
    let typeUrlParam = new URLSearchParams(window.location.search).get("type");

    if (typeUrlParam) {
      this.userType = typeUrlParam;
    }

    this.redirectFrom = this.$route.query.redirectFrom;
    this.fetchAllCouncils();
    this.fetchAllSpecialities();
  },
  methods: {
    ...mapActions("council", ["fetchAllCouncils"]),
    ...mapActions("speciality", ["fetchAllSpecialities"]),
    ...mapActions("auth", ["register"]),
    ...mapActions("user", [
      "emailVerification",
      "cpfVerification",
      "phoneNumberVerification"
    ]),

    debounce($v) {
      $v.$reset();
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v));
      }
      touchMap.set($v, setTimeout($v.$touch, 1000));
    },
    async handleSubmit() {
      let codeParam = new URLSearchParams(window.location.search).get("code");

      let user = {
        redirectFrom: this.redirectFrom,
        ...this.user,
        account_type: this.userType,
        name: this.user.name + " " + this.user.surname,
        cpf: this.user.cpf
          .replace("-", "")
          .replace(".", "")
          .replace(".", ""),
        phone_number: this.user.phone_number
          .replace("(", "")
          .replace(")", "")
          .replace("-", "")
      };

      if (this.userType == "doctor") {
        user.doctor_property_attributes = {
          ...this.doctor_property_attributes
        };
      }

      if (codeParam) {
        user.representant_code = codeParam;
      }

      this.register(user).then(path => {
        this.$router.push({ path });
      });
    }
  },
  computed: {
    ...mapGetters("council", ["councilOptions", "councils"]),
    ...mapGetters("speciality", ["specialitiesOptions"]),
    ...mapGetters("auth", ["isSubmitting", "redirectPathName"]),
    ...mapGetters("user", ["getCurrentUser"]),

    isPatientFormValid() {
      return !this.$v.user.$invalid && this.userType == "patient";
    },
    isDoctorFormValid() {
      return (
        !this.$v.user.$invalid &&
        !this.$v.doctor_property_attributes.$invalid &&
        this.userType == "doctor"
      );
    },
    isFormValid() {
      return this.isPatientFormValid || this.isDoctorFormValid;
    },
    isDoctor() {
      return isDoctor(this.getCurrentUser);
    },
    isPhysicianCouncil() {
      const council = this.councils.find(
        c => c.id === this.doctor_property_attributes.council_id
      );
      return !!council && council.name.startsWith("CRM");
    },
    canSubmit() {
      return this.isFormValid && this.terms;
    },
    ufsOptions() {
      return UFS.map(uf => ({
        label: uf,
        value: uf
      }));
    },
    genderOptions() {
      return [
        { label: "Feminino", value: "female" },
        { label: "Masculino", value: "male" },
        { label: "Prefiro não informar", value: "prefer_not_to_say" }
      ];
    },
    nameErrorMessages() {
      let errors = [];
      !this.$v.user.name.required && errors.push("Nome é obrigatório");
      return errors;
    },
    surnameErrorMessages() {
      let errors = [];
      !this.$v.user.surname.required && errors.push("Sobrenome é obrigatório");
      return errors;
    },
    emailErrorMessages() {
      let errors = [];
      !this.$v.user.email.required && errors.push("E-mail é obrigatório");
      !this.$v.user.email.email && errors.push("E-mail é inválido");
      !this.$v.user.email.isUnique && errors.push("Este e-mail já está em uso");
      !this.$v.user.email.hasValidChars &&
        errors.push(
          "Somente letras de (a - z), números de (0 - 9), e pontos (.) são permitidos"
        );
      return errors;
    },
    cpfErrorMessages() {
      let errors = [];
      !this.$v.user.cpf.required && errors.push("CPF é obrigatório");
      !this.$v.user.cpf.cpf && errors.push("CPF é inválido");
      !this.$v.user.cpf.isUnique && errors.push("Este CPF já está em uso");
      return errors;
    },
    birthdayErrorMessages() {
      let errors = [];

      if (!this.$v.user.birthday.required) {
        errors.push(BIRTHDAY_MESSAGES.required);
      } else if (!this.$v.user.birthday.validCalendarDate) {
        errors.push(BIRTHDAY_MESSAGES.invalid);
      } else if (!this.$v.user.birthday.notFutureDate) {
        errors.push(BIRTHDAY_MESSAGES.future);
      } else if (!this.$v.user.birthday.doctorMinimumAge) {
        errors.push(BIRTHDAY_MESSAGES.minimumAge);
      } else if (!this.$v.user.birthday.maxAge) {
        errors.push(BIRTHDAY_MESSAGES.invalid);
      }

      return errors;
    },
    PhoneNumberErrorMessages() {
      let errors = [];
      !this.$v.user.phone_number.required &&
        errors.push("Telefone é obrigatório");
      !this.$v.user.phone_number && errors.push("Telefone é inválido");
      !this.$v.user.phone_number.isUnique &&
        errors.push("Este Telefone já está em uso");
      return errors;
    },
    passwordErrorMessages() {
      return collectPasswordErrors(this.$v.user.password);
    },
    passwordComfirmationErrorMessages() {
      let errors = [];
      !this.$v.user.password_confirmation.required &&
        errors.push("Senha é obrigatória");
      !this.$v.user.password_confirmation.minLength &&
        errors.push("A senha deve conter no mínimo, 8 caracteres");
      !this.$v.user.password_confirmation.confirmPassword &&
        errors.push("As senhas não correspondem");
      return errors;
    },
    doctorCouncilErrorMessages() {
      let errors = [];
      !this.$v.doctor_property_attributes.council_id.required &&
        errors.push("Conselho é obrigatório");
      return errors;
    },
    doctorSpecialtyErrorMessages() {
      let errors = [];
      !this.$v.doctor_property_attributes.medical_specialty_id.required &&
        errors.push("Especialidade é obrigatório");
      return errors;
    },
    doctorCouncilUfErrorMessages() {
      let errors = [];
      !this.$v.doctor_property_attributes.council_uf.required &&
        errors.push("Estado é obrigatório");
      return errors;
    },
    doctorCouncilNumberErrorMessages() {
      let errors = [];
      !this.$v.doctor_property_attributes.council_number.required &&
        errors.push("Número do registro é obrigatório");
      return errors;
    },
    doctorShortBioErrorMessages() {
      let errors = [];
      !this.$v.doctor_property_attributes.short_bio.required &&
        errors.push("Breve descrição é obrigatória");
      return errors;
    }
  }
};
</script>

<style scoped>
.field-help-text {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 140%;
  color: #868e96;
}
</style>
